"use strict";

var currencies = require('./ui-currency');
var defaultCurrency = currencies.USD;
var currencyCodeRegExp = /[^\d.,\s-()]+/;
var valueRegExp = /[^\d.-]/g;
var parensRegExp = /^\(.+\)$/;

/**
 * Round to precision
 *
 * @param {Number} value
 * @param {Number} precision
 * @param {String or Function} [roundFn]: Fn or Fn on Math: 'ceil', 'round', or 'floor', defaults to Math.round
 * @return {String or Number}
 */
function roundd(value, precision, roundFn) {
  var pow = Math.pow(10, precision);
  var fn;
  if (typeof roundFn === 'function') {
    fn = roundFn;
  } else if (roundFn && Math[roundFn]) {
    fn = Math[roundFn];
  } else {
    fn = Math.round;
  }

  // the first toFixed(2) prevents the .9999 issue and is enough precision for the rounding function
  return parseFloat(fn((value * pow).toFixed(2)) / pow).toFixed(precision);
}

/**
 * Resolves a currency object from a given ISO 3-letter code or currency symbol.
 *
 * @param {String} currencyStringOrObj currency object or currency code (e.g. 'USD') or currency symbol (e.g. ''$')
 * @return {Object}
 */
function resolveCurrency(currencyStringOrObj) {
  var currency;
  if (!currencyStringOrObj) {
    currency = defaultCurrency;
  } else if (currencyStringOrObj.symbol && currencyStringOrObj.code) {
    // an existing currency obj was passed in, just return that.
    currency = currencyStringOrObj;
  } else {
    currency = currencies[currencyStringOrObj]; // try first by code
    if (!currency) {
      // now try by symbol
      Object.keys(currencies).forEach(function (key) {
        if (currencies[key].symbol === currencyStringOrObj) {
          currency = currencies[key];
        }
      });
    }
  }
  if (!currency) {
    currency = {
      code: currencyStringOrObj,
      symbol: currencyStringOrObj,
      formatDecimalSeperator: '.',
      formatThousandsSeperator: ',',
      formatDefaultDecimals: 2,
      formatString: '-¤#'
    };
    currencies[currencyStringOrObj] = currency;
  }
  return currency;
}

/**
 * Parses a given string into an object with 'value' and 'currencyCode' members if it can,
 * which can be passed into the UiMoney constructor to make an actual UiMoney object.
 * Sets 'value' to NaN and 'currencyCode' is default currency code if not resolved from the string.
 *
 * @param {String} moneyStr
 * @param {String} currency currency object or currency code (e.g. 'USD') or currency symbol (e.g. ''$')
 * @return {Object}
 */
function parse(moneyStr, currency) {
  var parsed;
  if (!moneyStr) {
    parsed = {
      value: NaN,
      currencyCode: ''
    };
  } else {
    var value = parseFloat(moneyStr.replace(valueRegExp, ''));
    if (parensRegExp.test(moneyStr)) {
      // set to negative if string in parens
      value *= -1;
    }
    var curStrMatches = currencyCodeRegExp.exec(moneyStr);
    var resolvedCurrency = resolveCurrency(curStrMatches ? curStrMatches[0] : currency);
    if (currency && resolvedCurrency.code !== resolveCurrency(currency).code) {
      // no valid value if currency parsed from string doesn't match specified currency
      value = NaN;
    }
    parsed = {
      value: value,
      currency: resolvedCurrency
    };
  }
  return parsed;
}

/**
 *
 * @param {UiMoney} money1
 * @param {UiMoney} money2
 * @return {Boolean} Whether the two UiMoney objects have the same currency code and valid values
 */
/* eslint no-restricted-globals: "error" */
function canCompare(money1, money2) {
  return money1.getCurrencyCode() === money2.getCurrencyCode() && !isNaN(money1.value) && !isNaN(money2.value);
}

/**
 * @param {Number|String|UiMoney} val
 * @return {Number} The number value of the given input
 */
function resolveValue(val) {
  return val.value !== undefined ? val.value : parseFloat(val);
}

/**
 * Constructor for UiMoney objects. Use parse() instead if need to create a UiMoney from
 * a combined string.
 *
 * @param {String|Number} value Numeric value or parse-able full money string
 * @param {String} currency currency object or currency code (e.g. 'USD') or currency symbol (e.g. ''$')
 */
function UiMoney(value, currency) {
  var val;
  var curr;
  if (typeof value === 'string') {
    // parse a string
    var parsedMoney = parse(value, currency);
    val = parsedMoney.value;
    curr = parsedMoney.currency;
  } else {
    val = parseFloat(value);
    curr = currency && currency.symbol ? currency : resolveCurrency(currency);
  }
  this.value = val;
  this.currency = curr;
  return this;
}

/**
 * For comparison functions, normalizes input to a UiMoney object if it can
 *
 * @param {String|UiMoney} possibleMoney
 * @return {UiMoney}
 */
function convertToMoney(possibleMoney) {
  return possibleMoney && possibleMoney instanceof UiMoney ? possibleMoney : new UiMoney(possibleMoney);
}

/**
 * Formats as a string using the formatString of the currency
 *
 * @param {Number} [precision]
 * @param {String} [overrideFormatString]
 * @param {String or Function} [roundFn]: Fn or Fn on Math: 'ceil', 'round', or 'floor', defaults to Math.round
 * @return {String}
 */
// eslint-disable-next-line no-multi-assign
UiMoney.prototype.format = UiMoney.prototype.toString = function format(precision, overrideFormatString, roundFn) {
  if (isNaN(this.value)) {
    return '';
  }

  // store pos/neg for later formatting
  var pos = this.value >= 0;
  var resolvedVal = Math.abs(this.value);

  // round
  var prec = typeof precision === 'number' ? precision : this.currency.formatDefaultDecimals;
  resolvedVal = roundd(resolvedVal, prec, roundFn);

  // format value w/ thousandsSep and dec separator
  var valueParts = resolvedVal.split('.');
  var base = valueParts[0];
  var mod = base.length > 3 ? base.length % 3 : 0;
  var tSep = this.currency.formatThousandsSeperator;
  var valueStr = (mod ? base.substr(0, mod) + tSep : '') + base.substr(mod).replace(/(\d{3})(?=\d)/g, "$1".concat(tSep));
  if (prec > 0) {
    valueStr += this.currency.formatDecimalSeperator + valueParts[1];
  }

  // replace parts into format string
  var formatted = overrideFormatString || this.currency.formatString; // start with formatString

  // insert value
  formatted = formatted.replace('#', valueStr);
  if (pos) {
    // remove negativity
    formatted = formatted.replace(/[-()]/g, '');
  }

  // replace currency symbol
  formatted = formatted.replace('¤', this.currency.symbol);

  // replace value
  return formatted;
};

/**
 * @return {String}
 */
UiMoney.prototype.getCurrencySymbol = function getCurrencySymbol() {
  return this.currency.symbol;
};

/**
 * @return {String}
 */
UiMoney.prototype.getCurrencyCode = function getCurrencyCode() {
  return this.currency.code;
};

/**
 *
 * @param {Number} Precision to round to
 * @return {Number} Can be NaN
 * @param {String or Function} [roundFn]: Fn or Fn on Math: 'ceil', 'round', or 'floor', defaults to Math.round
 */
UiMoney.prototype.getValue = function getValue(precision, roundFn) {
  return typeof precision !== 'undefined' ? roundd(this.value, precision, roundFn) : this.value;
};

/**
 * @param {Number|String|UiMoney} val
 */
UiMoney.prototype.add = function add(val) {
  this.value += resolveValue(val);
  return this;
};

/**
 * @param {Number|String|UiMoney} val
 */
UiMoney.prototype.subtract = function subtract(val) {
  this.value -= resolveValue(val);
  return this;
};

/**
 * @param {Number|String|UiMoney} val
 */
UiMoney.prototype.multiply = function multiply(val) {
  this.value *= resolveValue(val);
  return this;
};

/**
 * @param {Number|String|UiMoney} val
 */
UiMoney.prototype.divide = function divide(val) {
  this.value /= resolveValue(val);
  return this;
};

/**
 * @param {Number} precision
 * @param {String or Function} [roundFn]: Fn or Fn on Math: 'ceil', 'round', or 'floor', defaults to Math.round
 */
UiMoney.prototype.round = function round(precision, roundFn) {
  this.value = roundd(this.value, precision, roundFn);
  return this;
};

/**
 * @param {UiMoney|String} other Other UiMoney or properly formatted money string
 * @return {Boolean}
 */
UiMoney.prototype.equals = function equals(other) {
  var otherMoney = convertToMoney(other);
  if (!canCompare(this, otherMoney)) {
    return null;
  }
  return this.getValue() === otherMoney.getValue();
};

/**
 * @param {UiMoney|String} other Other UiMoney or properly formatted money string
 * @return {Boolean}
 */
UiMoney.prototype.lessThan = function lessThan(other) {
  var otherMoney = convertToMoney(other);
  if (!canCompare(this, otherMoney)) {
    return null;
  }
  return this.getValue() < otherMoney.getValue();
};

/**
 * @param {UiMoney|String} other Other UiMoney or properly formatted money string
 * @return {Boolean}
 */
UiMoney.prototype.greaterThan = function greaterThan(other) {
  var otherMoney = convertToMoney(other);
  if (!canCompare(this, otherMoney)) {
    return null;
  }
  return this.getValue() > otherMoney.getValue();
};
module.exports = UiMoney;
